import React, { useEffect, useRef } from 'react';
import { CollapseMenu } from 'cd-eft/components/common/CollapseMenu';
import { observer } from 'mobx-react';
import { Table, Modal, ModalHeader, ModalBody, ModalFooter, Container } from 'reactstrap';
import { ActionsMenu } from '../../common/ActionsMenu';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import { DropdownPopover } from 'cd-eft/components/common/DropdownPopover/DropdownPopover';
import { formatDateShort } from 'cd-eft/utils/formatDate';
import { mdiHelpCircleOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useHistory } from 'react-router-dom';
import stores from 'cd-eft/models/Stores';
import { PopupContainer } from '../../common/SummaryDetails/SDetails';
import { UGOSummaryVM, IUgoSummaryStation } from './UGOSummaryVM';
import { DesignSummaryButtons } from '../../common/DesignSummaryButtons';
import './UGOSummary.scss';
import { Loader, LoaderWithBar } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';

const { designStore, ugoDesignStore } = stores.AppStore;

const UGOSummaryComponent = () => {
  const history = useHistory();
  const vm = useRef(new UGOSummaryVM(history)).current;

  useEffect(() => {
    vm.combinedLoad();
    return () => {
      designStore.setLoader('');
    };
  }, []);

  const tooltipStandardMessage =
    'Data invalid for the listed items below. Input changes detected. Please Edit and recalculate.';

  const Cols = [
    { name: 'select', title: 'Select' },
    { name: 'name', title: 'UGO Name' },
    { name: 'alignmentName', title: 'Alignment Name' },
    { name: 'drainCount', title: '# of Drains' },
    { name: 'connected', title: 'Connected (Y/N)' },
    { name: 'warnings', title: 'Warnings', enableTooltip: true },
    { name: 'createdDate', title: 'Created Date' },
    { name: 'lastUpdatedDate', title: 'Last Updated' },
    { name: 'actions', title: 'Actions' },
  ];

  return (
    <>
      <UGOLoaderContainer />
      <div className="ugo-summary">
        <CollapseMenu
          showAsHeader={true}
          title={'Underground Outlet (UGO) - Summary'}
          className={`designelement-container`}
          noIcon={true}
          forceOpen={true}
        >
          <h2 className="summary-title">Summary</h2>
          <div className="design-summary-table">
            <Table className="set-table">
              <thead className="table-header">
                <tr>
                  {Cols.map((e, index) => (
                    <th key={'key-ugoSummary-title' + index} className={e.name === 'warnings' ? 'tooltip-header' : ''}>
                      <span>
                        {e.title}
                        {e.name === 'warnings' ? (
                          <>
                            &nbsp;
                            <StaticTooltip
                              tooltipMessage={tooltipStandardMessage}
                              tooltipSide="bottom"
                              id="ugo-warnings-tooltip"
                              title="Warnings info"
                            />
                          </>
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              {ugoDesignStore.ugoSummaryList.length > 0 ? (
                <tbody>
                  {ugoDesignStore.ugoSummaryList.map((el: IUgoSummaryStation, index: number) => (
                    <tr key={'key-UGOSummary-' + index}>
                      <td>
                        <input
                          id={'select' + index}
                          name={'select' + index}
                          type="checkbox"
                          checked={ugoDesignStore.ugoSummaryList[index].select}
                          onChange={() => vm.handleCheckboxChange(index)}
                          disabled={false}
                          aria-label={`select-${el.ugoName}`}
                        />
                      </td>
                      <td>{el.ugoName}</td>
                      <td>{el.alignmentName}</td>
                      <td>{el.numOfDrains}</td>
                      <td>{el.connected ? 'Yes' : 'No'}</td>
                      <td className="list-text">
                        {el.alignedElementDesignParamsId && vm.notificationStatusOR(el.alignedElementDesignParamsId) ? (
                          <NotificationBlock vm={vm} element={el} />
                        ) : (
                          'None'
                        )}
                      </td>
                      <td>{formatDateShort(el.createdDate)}</td>
                      <td>{formatDateShort(el.lastChangeDate)}</td>
                      <td>
                        <ActionsMenu i={index} item={el} elementActions={vm.elementActions} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : null}
            </Table>
            {!vm.isLoading && ugoDesignStore.ugoSummaryList.length === 0 ? (
              <h3 className="h4 note-summary">Add your first Underground Outlet (UGO)</h3>
            ) : null}
            {vm.isLoading ? <Loader /> : null}
          </div>

          <DesignSummaryButtons
            panel="UGO"
            disableAdd={false}
            buttontype={false}
            add={vm.newUGOProject}
            refreshElements={vm.refreshElements}
            rebuildSurface={ugoDesignStore.onClickSimulateRunoffToggle}
            disableRefreshElements={ugoDesignStore.ugoSummaryList.length === 0}
            disableRebuildSurface={ugoDesignStore.ugoSummaryList.length === 0}
            actionName="Network Simulation"
          />

          {vm.onClickInformation ? (
            <div>
              <Modal
                isOpen={vm.onClickInformation}
                toggle={vm.onClickInformationToggle}
                centered={true}
                className="eft-action-modal"
                aria-label={vm.headerTitle}
              >
                <ModalHeader toggle={vm.onClickInformationToggle} className="header-info-detail">
                  <div className="display-flex flex-align-center">
                    <strong>{`Information Details on ${vm.headerTitle}`}</strong>
                  </div>
                  <a className="href-info-detail" href="/design/ugosummarydetails" target="_blank">
                    <strong>Open Information in a New Window</strong>
                  </a>
                </ModalHeader>
                <ModalBody>
                  <Container>{PopupContainer({ title: vm.headerTitle, design: 3 })}</Container>
                </ModalBody>
                <ModalFooter>
                  <button className="usa-button usa-button--outline" onClick={vm.onClickInformationToggle}>
                    Close
                  </button>
                </ModalFooter>
              </Modal>
            </div>
          ) : null}
        </CollapseMenu>
      </div>
    </>
  );
};

// Add the UGOLoaderContainer component once we start getting UGO data
const UGOLoaderContainer = observer(() => {
  return (
    <>
      {designStore.isLoading && (
        <LoaderWithBar
          message={designStore.loadingMsg}
          className={'design-container-loader ugo-loader'}
          processName={designStore.processName}
          loadingProgress={designStore.loadingProgress}
          loading={designStore.isLoading}
        />
      )}
    </>
  );
});

interface INotificationBlockProps {
  vm: UGOSummaryVM;
  element;
}

interface INotificationEntryProps {
  vm: UGOSummaryVM;
  section: string;
}

const NotificationEntry = (props: INotificationEntryProps) => {
  return (
    <li className="list-content">
      <span className="text">{props.section}</span>
      <button
        className="question-button"
        title={props.section}
        onClick={() => props.vm.onClickInformationToggle(props.section)}
      >
        <Icon className="help-icon" path={mdiHelpCircleOutline} size={1} />
      </button>
    </li>
  );
};

const NotificationBlock = (props: INotificationBlockProps) => {
  const { vm, element } = props;

  return (
    <DropdownPopover buttonText="View List">
      <ul className="add-list-reset">
        {vm.notificationDictionary[element.alignedDesignElementId]?.surface !== 0 ? (
          <NotificationEntry vm={vm} section="Surface" />
        ) : null}
        {vm.notificationDictionary[element.alignedDesignElementId]?.alignment !== 0 ? (
          <NotificationEntry vm={vm} section="Alignment" />
        ) : null}
        {vm.notificationDictionary[element.alignedDesignElementId]?.hydrology !== 0 ? (
          <NotificationEntry vm={vm} section="Hydrology" />
        ) : null}
        {vm.notificationDictionary[element.alignedDesignElementId]?.designControl !== 0 ? (
          <NotificationEntry vm={vm} section="Design Controls" />
        ) : null}
        {vm.notificationDictionary[element.alignedDesignElementId]?.drains !== 0 ? (
          <NotificationEntry vm={vm} section="Drains" />
        ) : null}
      </ul>
    </DropdownPopover>
  );
};

export const UGOSummary = observer(UGOSummaryComponent);
